import { useQuery } from "@tanstack/react-query";
import { useSignedUrl } from "@/hooks/useSignedUrl";

export type SummarySection = {
  id: string;
  title: string;
  /** Optional -- present once the content-migration enrichment has run. */
  body_markdown?: string;
};

type RawEnrichedEntry = {
  id?: string;
  title?: string;
  body_markdown?: string;
  body?: string;
};

/**
 * Convert "1.1 Introduction" / "1.1 Introduction.txt" into a stable slug
 * usable as a React key and an anchor.
 */
function slugify(title: string): string {
  return title
    .replace(/\.txt$/i, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function stripExt(s: string): string {
  return s.replace(/\.txt$/i, "");
}

/**
 * Normalises the two manifest shapes the textbook viewer can encounter:
 *
 *   legacy (flat filenames):
 *     ["1.1 Introduction.txt", "1.2 Units.txt", ...]
 *
 *   enriched (post-migration):
 *     [{ id, title, body_markdown }, ...]
 *
 * Returns a unified `SummarySection[]`. `body_markdown` is undefined for
 * legacy entries, which `SummaryPanel` renders as a "coming soon" state.
 */
function normaliseManifest(raw: unknown): SummarySection[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, idx): SummarySection | null => {
      if (typeof entry === "string") {
        const title = stripExt(entry).trim();
        if (!title) return null;
        return { id: slugify(title) || `section-${idx + 1}`, title };
      }
      if (entry && typeof entry === "object") {
        const e = entry as RawEnrichedEntry;
        const title = (e.title ?? "").trim();
        if (!title) return null;
        return {
          id: (e.id || slugify(title) || `section-${idx + 1}`),
          title,
          body_markdown: e.body_markdown ?? e.body,
        };
      }
      return null;
    })
    .filter((s): s is SummarySection => s !== null);
}

export type UseSummariesManifestResult = {
  sections: SummarySection[];
  isLoading: boolean;
  isError: boolean;
  /** True when the manifest is missing OR the API said locked. We treat
   *  both as "no summaries to show" rather than an error -- a chapter
   *  without summaries is a normal state, not a failure. */
  isUnavailable: boolean;
};

/**
 * Loads + parses the per-chapter section summaries manifest.
 *
 * Pipeline: useSignedUrl (presigned S3 URL via backend) -> fetch JSON ->
 * normalise to `SummarySection[]`. Always returns a value-shaped result;
 * callers don't need to special-case the loading/error states beyond
 * deciding whether to render the section list.
 */
export function useSummariesManifest(
  chapterId: string | undefined,
  enabled = true,
): UseSummariesManifestResult {
  const { url, isLoading: urlLoading, isError: urlError, isLocked } = useSignedUrl({
    chapterId,
    assetType: "summaries-manifest",
    enabled,
  });

  const jsonQuery = useQuery({
    queryKey: ["summaries-manifest", "json", chapterId, url ?? null],
    enabled: !!url,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<SummarySection[]> => {
      const res = await fetch(url!, { method: "GET" });
      if (!res.ok) throw new Error(`Manifest fetch failed: ${res.status}`);
      const raw = (await res.json()) as unknown;
      return normaliseManifest(raw);
    },
    retry: 0,
  });

  const sections = jsonQuery.data ?? [];
  const isLoading = urlLoading || (!!url && jsonQuery.isLoading);
  const isError = urlError || jsonQuery.isError;
  const isUnavailable = isLocked || (!isLoading && !isError && sections.length === 0);

  return { sections, isLoading, isError, isUnavailable };
}
